/**
 * A catalogue of the vanilla Bedrock ITEM TAGS that show up in crafting
 * recipes, used to populate the recipe item picker when a slot is set to
 * "Vanilla tag".
 *
 * A tag slot means "any item carrying this tag" - `{"tag": "minecraft:planks"}`
 * is how vanilla writes "any planks" - as opposed to one specific item.
 *
 * Like the item catalogue this is curated rather than exhaustive, and the
 * picker still accepts any hand-typed `namespace:name` tag, so a tag missing
 * here is never a dead end.
 */

export interface VanillaTag {
  /** Full tag id, e.g. "minecraft:planks". */
  id: string;
  /** Human label, e.g. "Any Planks". */
  name: string;
  /** Label of the group the tag belongs to. */
  group: string;
}

type Entry = [namePart: string, label: string];

const GROUPS: { label: string; entries: Entry[] }[] = [
  {
    label: "Wood",
    entries: [
      ["planks", "Any Planks"],
      ["logs", "Any Log"],
      ["logs_that_burn", "Any Burnable Log"],
      ["wooden_slabs", "Any Wooden Slab"],
      ["wooden_stairs", "Any Wooden Stairs"],
      ["wooden_pressure_plates", "Any Wooden Pressure Plate"],
      ["wooden_buttons", "Any Wooden Button"],
      ["wooden_fences", "Any Wooden Fence"],
      ["wooden_doors", "Any Wooden Door"],
      ["wooden_trapdoors", "Any Wooden Trapdoor"],
      ["door", "Any Door"],
      ["trapdoors", "Any Trapdoor"],
      ["stairs", "Any Stairs"],
      ["slab", "Any Slab"],
      ["fence", "Any Fence"],
      ["fence_gate", "Any Fence Gate"],
    ],
  },
  {
    label: "Materials",
    entries: [
      ["stone_crafting_materials", "Any Stone Crafting Material"],
      ["stone_tool_materials", "Any Stone Tool Material"],
      ["planks_crafting_materials", "Any Plank Crafting Material"],
      ["coals", "Any Coal"],
      ["sand", "Any Sand"],
      ["wool", "Any Wool"],
      ["banner", "Any Banner"],
      ["boats", "Any Boat"],
      ["chest_boats", "Any Chest Boat"],
      ["boat", "Any Boat (legacy)"],
      ["arrow", "Any Arrow"],
      ["bookshelf_books", "Any Bookshelf Book"],
      ["soul_fire_base_blocks", "Any Soul Fire Base Block"],
    ],
  },
  {
    label: "Equipment",
    entries: [
      ["is_sword", "Any Sword"],
      ["is_axe", "Any Axe"],
      ["is_pickaxe", "Any Pickaxe"],
      ["is_shovel", "Any Shovel"],
      ["is_hoe", "Any Hoe"],
      ["is_tool", "Any Tool"],
      ["is_armor", "Any Armor"],
      ["chainmail_tier", "Any Chainmail Armor"],
      ["leather_tier", "Any Leather Armor"],
      ["iron_tier", "Any Iron Tier Gear"],
      ["golden_tier", "Any Golden Tier Gear"],
      ["diamond_tier", "Any Diamond Tier Gear"],
      ["netherite_tier", "Any Netherite Tier Gear"],
      ["is_trident", "Any Trident"],
      ["digger", "Any Digging Tool"],
    ],
  },
  {
    label: "Nature",
    entries: [
      ["crops", "Any Crop"],
      ["flowers", "Any Flower"],
      ["small_flowers", "Any Small Flower"],
      ["tall_flowers", "Any Tall Flower"],
      ["leaves", "Any Leaves"],
      ["saplings", "Any Sapling"],
      ["vibration_damper", "Any Vibration Damper"],
    ],
  },
];

/** Every catalogued tag, flattened and de-duplicated by id, in group order. */
export const VANILLA_TAGS: VanillaTag[] = (() => {
  const seen = new Set<string>();
  const out: VanillaTag[] = [];
  for (const group of GROUPS) {
    for (const [namePart, name] of group.entries) {
      const id = `minecraft:${namePart}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ id, name, group: group.label });
    }
  }
  return out;
})();

const BY_ID = new Map(VANILLA_TAGS.map((tag) => [tag.id, tag]));

/** Looks up a catalogued tag by full id. Bare name-parts are assumed vanilla. */
export function findVanillaTag(id: string): VanillaTag | undefined {
  const full = id.includes(":") ? id : `minecraft:${id}`;
  return BY_ID.get(full.trim().toLowerCase());
}
